import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Eye, EyeOff, UserPlus, Building, Mail, Phone, Lock, CheckCircle, Zap, XCircle } from 'lucide-react';
import './Register.css';

const Register = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    organizationName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
    if (e.target.name === 'password') {
      validatePasswordRealTime(e.target.value);
    }
  };

  const validatePasswordRealTime = (password) => {
    const errors = [];
    if (password.length < 6) errors.push('At least 6 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    setPasswordErrors(errors);
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) errors.push('at least 6 characters');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const errors = validatePassword(form.password);
    if (errors.length > 0) {
      setError(`Password must contain: ${errors.join(', ')}`);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await register({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        password: form.password,
        organizationName: form.organizationName
      });

      // Navigate to verify-phone 
      setSuccess('Account created! Sending verification code to your phone...');
      setTimeout(() => {
        navigate('/verify-phone');
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-bg">
        <div className="register-bg-gradient"></div>
        <div className="register-bg-pattern"></div>
      </div>

      <div className="register-grid">
        <div className="register-info">
          <div className="info-content">
            <div className="info-logo">
              <div className="logo-icon">J</div>
              <div>
                <div className="logo-name">Jipate</div>
                <div className="logo-tagline">WhatsApp Business Platform</div>
              </div>
            </div>

            <h1 className="info-title">
              <span className="info-title-white">Start growing your</span><br />
              <span className="info-title-white">business on</span> <span className="info-gradient">WhatsApp</span>
            </h1>

            <p className="info-desc">
              Join thousands of businesses already using our platform to reach more customers and increase sales.
            </p>

            <div className="info-features">
              <div className="info-feature">
                <div className="feature-icon success">
                  <CheckCircle size={18} />
                </div>
                <div>
                  <h4 className="feature-title-white">Free to get started</h4>
                  <p className="feature-desc-white">No credit card required</p>
                </div>
              </div>
              <div className="info-feature">
                <div className="feature-icon primary">
                  <Zap size={18} />
                </div>
                <div>
                  <h4 className="feature-title-white">Instant setup</h4>
                  <p className="feature-desc-white">Get started in minutes</p>
                </div>
              </div>
              <div className="info-feature">
                <div className="feature-icon purple">
                  <Building size={18} />
                </div>
                <div>
                  <h4 className="feature-title-white">For all businesses</h4>
                  <p className="feature-desc-white">From startups to enterprises</p>
                </div>
              </div>
            </div>

            <div className="info-testimonial">
              <div className="testimonial-quote">"</div>
              <p className="testimonial-text">
                Jipate transformed our customer communication. We've seen a 40% increase in engagement.
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">JM</div>
                <div>
                  <div className="author-name">John Mwangi</div>
                  <div className="author-title">CEO, Kilimanjaro Bank</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="register-form-wrapper">
          <div className="register-card animate-slide-up">
            <div className="form-header">
              <div className="form-icon">
                <UserPlus size={24} />
              </div>
              <h2 className="form-title">Create an account</h2>
              <p className="form-subtitle">Join the WhatsApp Business Platform</p>
            </div>

            {success && (
              <div className="alert-success animate-fade-in">
                <CheckCircle size={16} />
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="alert-error animate-fade-in">
                <XCircle size={14} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="register-form">
              <div className="form-row">
                <div className="input-group">
                  <label className="input-label">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-input"
                    placeholder="John"
                    value={form.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-input"
                    placeholder="Doe"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Organization Name</label>
                <div className="input-icon-wrapper">
                  <Building size={18} className="input-icon" />
                  <input
                    type="text"
                    name="organizationName"
                    className="form-input with-icon"
                    placeholder="Your Company Name"
                    value={form.organizationName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Email Address</label>
                <div className="input-icon-wrapper">
                  <Mail size={18} className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    className="form-input with-icon"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Phone Number</label>
                <div className="input-icon-wrapper">
                  <Phone size={18} className="input-icon" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    className="form-input with-icon"
                    placeholder="+255XXXXXXXXX"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-hint">Tanzania format: +255 followed by 9 digits</div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label className="input-label">Password</label>
                  <div className="password-wrapper">
                    <Lock size={18} className="password-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="form-input with-password-icon"
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="input-hint">
                    {passwordErrors.length > 0 && (
                      <div className="password-requirements">
                        <span className="hint-label">Requirements:</span>
                        {passwordErrors.map((err, idx) => (
                          <span key={idx} className="hint-badge">{err}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Confirm Password</label>
                  <div className="password-wrapper">
                    <Lock size={18} className="password-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      className={`form-input with-password-icon ${
                        form.confirmPassword && form.password !== form.confirmPassword ? 'input-error' : ''
                      }`}
                      placeholder="Re-enter your password"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <div className="input-error-text">Passwords do not match</div>
                  )}
                  {form.confirmPassword && form.password === form.confirmPassword && form.password.length > 0 && (
                    <div className="input-success-text">✓ Passwords match</div>
                  )}
                </div>
              </div>

              <button type="submit" className="btn-register" disabled={loading}>
                {loading ? (
                  <span className="btn-loading">
                    <span className="spinner-small"></span>
                    Creating account...
                  </span>
                ) : (
                  <>
                    <UserPlus size={18} />
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;